import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, ArrowLeft, CheckCircle2, Home } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Reset link sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="w-full max-w-md">
        <div className="flex items-center justify-between">
          <BrandLogo />
          <Link to="/" data-testid="home-button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-amber-600"><Home className="h-4 w-4" /> Home</Link>
        </div>
        {sent ? (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="forgot-sent">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-slate-600">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link to that address.
              The link expires in 1 hour.
            </p>
            <Link to="/login">
              <Button className="mt-6 w-full gap-2 bg-slate-900 hover:bg-slate-800" data-testid="back-to-login-button">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-8 font-display text-3xl font-bold text-slate-900">Forgot password?</h1>
            <p className="mt-1 text-slate-500">Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required placeholder="you@company.co.za" className="mt-1.5" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                className="w-full gap-2 bg-slate-900 py-6 text-base font-semibold hover:bg-slate-800">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send reset link
              </Button>
            </form>
            <Link to="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700" data-testid="forgot-back-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
